import { useEffect, useRef } from "react";

/**
 * Mild celebratory loop, drawn on a full-viewport canvas. Runs for as long as
 * it stays mounted — the parent only renders this while the celebrated
 * condition holds, so unmounting it is how the loop stops. Self-contained (no
 * confetti dependency) since this only fires on a single hidden dashboard
 * state and the effect itself is cheap enough that a library isn't worth it.
 */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  born: number;
  life: number;
}

const BURST_COLOR_VARS = ["--h-ok-fg", "--h-warn-fg", "--h-primary", "--h-active-fg"];
const BURST_INTERVAL_MS = 15_000;
const GRAVITY = 0.05;
const DRAG = 0.985;

export function Fireworks() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const rootStyle = getComputedStyle(document.documentElement);
    const colors = BURST_COLOR_VARS.map((name) => rootStyle.getPropertyValue(name).trim() || "#3ee089");

    const particles: Particle[] = [];
    function spawnBurst() {
      const cx = window.innerWidth * (0.3 + Math.random() * 0.4);
      const cy = window.innerHeight * (0.18 + Math.random() * 0.18);
      const count = 34;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.3;
        const speed = 2 + Math.random() * 3;
        particles.push({
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: colors[i % colors.length]!,
          born: performance.now(),
          life: 900 + Math.random() * 400
        });
      }
    }

    // One burst right away as immediate feedback, then a single slow burst
    // every BURST_INTERVAL_MS for as long as this stays mounted.
    spawnBurst();
    const interval = window.setInterval(spawnBurst, BURST_INTERVAL_MS);

    let raf = 0;
    function tick(now: number) {
      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]!;
        const age = now - p.born;
        if (age >= p.life) {
          particles.splice(i, 1);
          continue;
        }
        p.vy += GRAVITY;
        p.vx *= DRAG;
        p.vy *= DRAG;
        p.x += p.vx;
        p.y += p.vy;
        ctx!.globalAlpha = Math.max(1 - age / p.life, 0);
        ctx!.fillStyle = p.color;
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, 2.4, 0, Math.PI * 2);
        ctx!.fill();
      }
      ctx!.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(interval);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 60, pointerEvents: "none" }} aria-hidden="true" />
  );
}
